<template>
  <div v-redirect-if-no-permission:VIEW_FINANCE_REPORTS class="fin-page">
    <finance-section-nav />
    <loading-component :value="loading" />

    <v-card>
      <div class="fin-header fin-header--sticky fr-no-print">
        <div class="fin-toolbar">
          <span class="fin-title">{{ t("title") }}</span>
          <v-spacer></v-spacer>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!trips.length" @click="expandAll">
              <v-icon small>unfold_more</v-icon>
            </v-btn>
            <span>{{ c("expandAll") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!trips.length" @click="collapseAll">
              <v-icon small>unfold_less</v-icon>
            </v-btn>
            <span>{{ c("collapseAll") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!report" @click="exportExcel">
              <v-icon small>grid_on</v-icon>
            </v-btn>
            <span>{{ c("exportExcel") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!report" @click="downloadCsv">
              <v-icon small>file_download</v-icon>
            </v-btn>
            <span>{{ c("exportCsv") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat :disabled="!report" @click="exportPrint">
              <v-icon small>print</v-icon>
            </v-btn>
            <span>{{ c("print") }}</span>
          </v-tooltip>
          <v-tooltip bottom>
            <v-btn slot="activator" icon small flat color="primary" :disabled="!filters.captain_id" @click="loadReport">
              <v-icon small>refresh</v-icon>
            </v-btn>
            <span>{{ c("refresh") }}</span>
          </v-tooltip>
        </div>
        <div class="fin-filters">
          <driver-statement-captain-picker v-model="filters.captain_id" :preset="pickerPreset" />
          <v-text-field v-model="filters.from" type="date" :label="c('from')" hide-details class="fin-field"></v-text-field>
          <v-text-field v-model="filters.to" type="date" :label="c('to')" hide-details class="fin-field"></v-text-field>
          <v-btn small color="primary" :disabled="!filters.captain_id" @click="loadReport">{{ c("apply") }}</v-btn>
        </div>
      </div>

      <div v-if="!report && !loading" class="ds-empty">{{ t("chooseCaptainFirst") }}</div>

      <template v-if="report">
        <div
          v-for="(alert, index) in report.alerts"
          :key="'alert-' + index"
          :class="alert.level === 'error' ? 'fin-alert-error' : 'ds-alert-warn'"
        >
          <v-icon small :color="alert.level === 'error' ? '#c62828' : '#e65100'">warning</v-icon>
          {{ alert.text }}
        </div>

        <!-- Driver header (her p5 header line + opening / closing balance) -->
        <div class="ds-section">
          <div class="ds-section-title">{{ t("driverInfo") }}</div>
          <div class="ds-info-grid">
            <div v-for="line in driverLines" :key="line.key" class="ds-info-item">
              <span class="ds-info-label">{{ line.label }}</span>
              <span class="ds-info-value" :class="{ 'fin-num': line.numeric }">{{ line.value }}</span>
              <span v-if="line.hint" class="ds-info-hint">{{ line.hint }}</span>
            </div>
          </div>
        </div>

        <!-- Trips -->
        <div class="ds-section">
          <div class="ds-section-title">
            {{ t("tripsTitle") }}
            <span class="ds-count">({{ t("tripsCount") }}: {{ trips.length }})</span>
          </div>
          <div class="ds-table-wrap fin-table-wrap">
            <table class="ds-table fin-table">
              <thead>
                <tr>
                  <th class="ds-toggle-col fr-no-print"></th>
                  <th v-for="column in tripColumns" :key="column.key" :class="{ 'fin-num-head': isNumeric(column) }">
                    {{ column.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <template v-for="trip in trips">
                  <tr :key="'trip-' + trip.accounting_id" :class="{ 'ds-row-warn': trip.gl_mismatch }">
                    <td class="ds-toggle-col fr-no-print">
                      <v-btn icon small class="ds-toggle" @click="toggle(trip.accounting_id)">
                        <v-icon small>{{ expanded[trip.accounting_id] ? "remove" : "add" }}</v-icon>
                      </v-btn>
                    </td>
                    <td class="fin-code">
                      #{{ trip.booking_id }}
                      <div class="ds-sub">{{ formatStamp(trip.settled_at, "") }}</div>
                    </td>
                    <td>
                      {{ trip.type_label }}
                      <v-icon v-if="trip.gl_mismatch" small color="deep-orange" :title="t('flag.mismatch')">warning</v-icon>
                    </td>
                    <td>
                      {{ trip.payment_label || trip.payment_method_name || "—" }}
                      <div v-if="trip.payment_detail" class="ds-sub">{{ trip.payment_detail }}</div>
                    </td>
                    <td class="fin-num">{{ money(trip.trip_total) }}</td>
                    <td class="fin-code">{{ trip.driver_account_code || "—" }}</td>
                    <td class="fin-code">{{ trip.owner_account_code || "-" }}</td>
                    <td class="fin-num">{{ money(trip.company_share) }}</td>
                    <td class="fin-num">{{ trip.owner_share ? money(trip.owner_share) : "-" }}</td>
                    <td class="fin-num">{{ money(trip.driver_share) }}</td>
                    <td class="fin-num ds-plus">{{ signed(trip.bonus, "+") }}</td>
                    <td class="fin-num ds-minus">{{ signed(trip.fine, "-") }}</td>
                    <td class="fin-num ds-bold">{{ money(trip.net_driver_share) }}</td>
                    <td class="fin-num ds-bold">{{ money(trip.cash_received) }}</td>
                    <td class="fin-num ds-bold">{{ money(trip.due_from_company_driver) }}</td>
                    <td class="fin-num ds-bold">{{ money(trip.due_from_company_owner) }}</td>
                    <td class="fin-num ds-extra">{{ money(trip.due_to_company_cash) }}</td>
                  </tr>
                  <tr v-if="expanded[trip.accounting_id]" :key="'detail-' + trip.accounting_id" class="ds-detail-row">
                    <td class="fr-no-print"></td>
                    <td :colspan="tripColumns.length">
                      <div class="ds-detail-grid">
                        <div v-for="item in tripDetail(trip)" :key="item.label" class="ds-detail-item">
                          <span class="ds-info-label">{{ item.label }}</span>
                          <span :class="{ 'fin-num': item.numeric, 'ds-warn-text': item.warn }">{{ item.value }}</span>
                        </div>
                      </div>
                    </td>
                  </tr>
                </template>
                <tr v-if="!trips.length">
                  <td :colspan="tripColumns.length + 1" class="ds-empty-cell">{{ c("noMovements") }}</td>
                </tr>
              </tbody>
              <tfoot v-if="trips.length">
                <tr class="ds-total-row">
                  <td class="fr-no-print"></td>
                  <td>{{ c("total") }}</td>
                  <td></td>
                  <td></td>
                  <td class="fin-num">{{ money(totals.trip_total) }}</td>
                  <td></td>
                  <td></td>
                  <td class="fin-num">{{ money(totals.company_share) }}</td>
                  <td class="fin-num">{{ money(totals.owner_share) }}</td>
                  <td class="fin-num">{{ money(totals.driver_share) }}</td>
                  <td class="fin-num">{{ signed(totals.bonus, "+") }}</td>
                  <td class="fin-num">{{ signed(totals.fine, "-") }}</td>
                  <td class="fin-num">{{ money(totals.net_driver_share) }}</td>
                  <td class="fin-num">{{ money(totals.cash_received) }}</td>
                  <td class="fin-num">{{ money(totals.due_from_company_driver) }}</td>
                  <td class="fin-num">{{ money(totals.due_from_company_owner) }}</td>
                  <td class="fin-num">{{ money(totals.due_to_company_cash) }}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <!-- Other movements on the driver's accounts -->
        <div class="ds-section">
          <div class="ds-section-title">{{ t("otherTitle") }}</div>
          <div class="ds-table-wrap fin-table-wrap">
            <table class="ds-table fin-table">
              <thead>
                <tr>
                  <th v-for="column in otherColumns" :key="column.key" :class="{ 'fin-num-head': isNumeric(column) }">
                    {{ column.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(move, index) in otherMovements"
                  :key="'other-' + index"
                  :class="{ 'ds-row-muted': !move.gl_posted }"
                >
                  <td>{{ move.journal_date }}</td>
                  <td class="fin-code">{{ move.reference_no || "—" }}</td>
                  <td class="ds-wrap">{{ move.description }}</td>
                  <td class="fin-code">{{ move.account_code || "—" }}</td>
                  <td class="fin-num">{{ move.debit ? money(move.debit) : "" }}</td>
                  <td class="fin-num">{{ move.credit ? money(move.credit) : "" }}</td>
                  <td class="ds-wrap">{{ moveRemark(move) }}</td>
                </tr>
                <tr v-if="!otherMovements.length">
                  <td :colspan="otherColumns.length" class="ds-empty-cell">{{ c("noMovements") }}</td>
                </tr>
              </tbody>
              <tfoot v-if="otherMovements.length">
                <tr class="ds-total-row">
                  <td>{{ c("total") }}</td>
                  <td></td>
                  <td></td>
                  <td></td>
                  <td class="fin-num">{{ money(report.other_totals.debit) }}</td>
                  <td class="fin-num">{{ money(report.other_totals.credit) }}</td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        <!-- GL balances + reconciliation -->
        <div class="ds-section ds-two-col">
          <div>
            <div class="ds-section-title">{{ t("balancesTitle") }}</div>
            <table class="ds-table fin-table">
              <thead>
                <tr>
                  <th v-for="column in balanceColumns" :key="column.key" :class="{ 'fin-num-head': isNumeric(column) }">
                    {{ column.label }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in balanceRows" :key="'bal-' + row.code" :class="{ 'ds-total-row': row._kind === 'total' }">
                  <td class="fin-code">{{ row.code }}</td>
                  <td>{{ row.name }}</td>
                  <td class="fin-num">{{ money(row.opening) }}</td>
                  <td class="fin-num">{{ money(row.debit) }}</td>
                  <td class="fin-num">{{ money(row.credit) }}</td>
                  <td class="fin-num">{{ money(row.closing) }}</td>
                  <td>{{ row.side }}</td>
                </tr>
                <tr v-if="!balanceRows.length">
                  <td :colspan="balanceColumns.length" class="ds-empty-cell">{{ c("noData") }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div>
            <div class="ds-section-title">{{ t("reconciliationTitle") }}</div>
            <table class="ds-table fin-table">
              <tbody>
                <tr v-for="row in reconciliationRows" :key="'rec-' + row.key" :class="{ 'ds-total-row': row._bold }">
                  <td>{{ row.label }}</td>
                  <td class="fin-num">{{ money(row.amount) }}</td>
                </tr>
                <tr>
                  <td colspan="2" :class="report.reconciliation.is_reconciled ? 'ds-ok' : 'ds-bad'">
                    <v-icon small :color="report.reconciliation.is_reconciled ? 'green' : 'red'">
                      {{ report.reconciliation.is_reconciled ? "check_circle" : "warning" }}
                    </v-icon>
                    {{ report.reconciliation.is_reconciled ? t("rec.reconciled") : t("rec.notReconciled") }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div v-if="report.notes && report.notes.length" class="ds-section ds-notes">
          <div class="ds-section-title">{{ t("notesTitle") }}</div>
          <ul>
            <li v-for="(note, index) in report.notes" :key="'note-' + index">{{ note }}</li>
          </ul>
        </div>
      </template>
    </v-card>
  </div>
</template>

<script>
/*
 * Report 2.2 of the accountant's document 2026-10-03 (driver-statement):
 * her p5 table (one row per settled trip, her 16 columns + the cash owed to
 * the company), «حركات أخرى» (every other movement on the driver's GL
 * accounts), the GL balances and the reconciliation opening + movements =
 * closing.
 * API: this.api.ACTIONS.ACCOUNTING_DRIVER_STATEMENT (GET, format=json|csv).
 * Money in the payload is in STORED units; formatMoney / the export mixin
 * divide by 100.
 */
import FinanceSectionNav from "@/components/finance/FinanceSectionNav";
import LoadingComponent from "@/components/LoadingComponent";
import DriverStatementCaptainPicker from "@/components/finance/DriverStatementCaptainPicker";
import financeReportExport from "@/mixins/financeReportExport";
import { formatFinanceDate } from "@/mixins/financeDate";
import { companyToday } from "@/util/companyTime";

// Final-version doc 2026-10-05: the company (Damascus) day on the
// server-synced clock, not the PC calendar.
function today() {
  return companyToday();
}

export default {
  name: "DriverStatement",
  title() {
    return this.$t("finance.reports2.driverStatement.pageTitle");
  },
  components: { FinanceSectionNav, LoadingComponent, DriverStatementCaptainPicker },
  mixins: [financeReportExport],
  data() {
    const end = today();
    return {
      loading: false,
      report: null,
      expanded: {},
      pickerPreset: null,
      filters: {
        captain_id: null,
        from: end.slice(0, 8) + "01",
        to: end,
      },
    };
  },
  computed: {
    trips() {
      return (this.report && this.report.trips) || [];
    },
    totals() {
      return (this.report && this.report.trip_totals) || {};
    },
    otherMovements() {
      return (this.report && this.report.other_movements) || [];
    },
    tripColumns() {
      const col = (key, label, type, width) => ({ key, label: this.t("col." + label), type, width });
      return [
        col("trip", "trip", "text", 22),
        col("type_label", "type", "text", 14),
        col("payment_label", "paymentMethod", "text", 18),
        col("trip_total", "tripTotal", "money"),
        col("driver_account_code", "driverAccount", "code", 20),
        col("owner_account_code", "ownerAccount", "code", 14),
        col("company_share", "companyShare", "money"),
        col("owner_share", "ownerShare", "money"),
        col("driver_share", "driverShare", "money"),
        col("bonus", "bonus", "money"),
        col("fine", "fine", "money"),
        col("net_driver_share", "netDriverShare", "money"),
        col("cash_received", "cashReceived", "money"),
        col("due_from_company_driver", "dueDriver", "money"),
        col("due_from_company_owner", "dueOwner", "money"),
        col("due_to_company_cash", "dueToCompany", "money"),
      ];
    },
    otherColumns() {
      const col = (key, label, type, width) => ({ key, label: this.t("col." + label), type, width });
      return [
        col("journal_date", "date", "date", 20),
        col("reference_no", "reference", "code", 18),
        col("description", "description", "text", 40),
        col("account_code", "account", "code", 20),
        col("debit", "debit", "money"),
        col("credit", "credit", "money"),
        col("remark", "remark", "text", 30),
      ];
    },
    balanceColumns() {
      const col = (key, label, type, width) => ({ key, label: this.t("col." + label), type, width });
      return [
        col("code", "account", "code", 20),
        col("name", "description", "text", 36),
        col("opening", "opening", "money"),
        col("debit", "debit", "money"),
        col("credit", "credit", "money"),
        col("closing", "closing", "money"),
        col("side", "side", "text", 24),
      ];
    },
    balanceRows() {
      if (!this.report) {
        return [];
      }
      const balances = this.report.balances;
      const rows = (balances.by_account || []).map((account) => ({
        code: account.code,
        name: account.name + " - " + this.t("role." + account.role),
        opening: account.opening,
        debit: account.debit,
        credit: account.credit,
        closing: account.closing,
        side: account.closing_side,
        _highlight: account.alert ? "warn" : undefined,
      }));
      if (rows.length) {
        rows.push({
          code: "",
          name: this.c("total"),
          opening: balances.opening,
          debit: balances.debit,
          credit: balances.credit,
          closing: balances.closing,
          side: balances.closing_side,
          _kind: "total",
        });
      }
      return rows;
    },
    reconciliationRows() {
      if (!this.report) {
        return [];
      }
      const r = this.report.reconciliation;
      return [
        { key: "opening", label: this.t("rec.opening"), amount: r.opening },
        { key: "rideCredit", label: this.t("rec.rideCredit"), amount: r.ride_credit },
        { key: "rideDebit", label: this.t("rec.rideDebit"), amount: r.ride_debit },
        { key: "otherCredit", label: this.t("rec.otherCredit"), amount: r.other_credit },
        { key: "otherDebit", label: this.t("rec.otherDebit"), amount: r.other_debit },
        { key: "computed", label: this.t("rec.computedClosing"), amount: r.computed_closing, _bold: true },
        { key: "closing", label: this.t("rec.closing"), amount: r.closing, _bold: true },
        { key: "difference", label: this.t("rec.difference"), amount: r.difference },
        { key: "trips", label: this.t("rec.tripsEffect"), amount: this.totals.driver_balance_effect },
      ];
    },
    driverLines() {
      if (!this.report) {
        return [];
      }
      const d = this.report.driver;
      const b = this.report.balances;
      const lines = [
        { key: "name", label: this.t("name"), value: (d.name || "—") + " (#" + d.id + ")" },
        { key: "phone", label: this.t("phone"), value: d.phone || "—" },
        { key: "leaf", label: this.t("driverAccount"), value: (d.leaf_codes || []).join(" / ") || this.t("noLeaf") },
        {
          key: "car",
          label: this.t("carModel"),
          value: d.car ? d.car.model_label + (d.car.plate_number ? " - " + d.car.plate_number : "") : this.t("noCar"),
        },
      ];
      if (d.owner) {
        lines.push({ key: "owner", label: this.t("owner"), value: d.owner.name || "—" });
        lines.push({ key: "ownerLeaf", label: this.t("ownerAccount"), value: d.owner.leaf_code || this.t("noLeaf") });
      }
      lines.push({ key: "opening", label: this.t("openingBalance"), value: this.money(b.opening) + " - " + b.opening_side, numeric: true });
      lines.push({ key: "closing", label: this.t("closingBalance"), value: this.money(b.closing) + " - " + b.closing_side, numeric: true });
      if (d.wallet_balance !== null && d.wallet_balance !== undefined) {
        lines.push({
          key: "wallet",
          label: this.t("walletBalance"),
          value: this.money(d.wallet_balance),
          numeric: true,
          hint: this.t("walletNote"),
        });
      }
      return lines;
    },
  },
  mounted() {
    const query = this.$route ? this.$route.query || {} : {};
    if (query.from) this.filters.from = String(query.from);
    if (query.to) this.filters.to = String(query.to);
    if (query.captain_id && Number(query.captain_id) > 0) {
      this.filters.captain_id = Number(query.captain_id);
      this.loadReport();
    }
  },
  methods: {
    t(key) {
      return this.$t("finance.reports2.driverStatement." + key);
    },
    c(key) {
      return this.$t("finance.reports2.common." + key);
    },
    money(value) {
      return this.formatMoney(value || 0);
    },
    // Final-version doc 2026-10-05: server timestamps in company (Damascus)
    // time even if a zoned value arrives; seconds kept as before.
    formatStamp(value, fallback) {
      return value === null || value === undefined || value === "" ? fallback : formatFinanceDate(value, true);
    },
    signed(value, sign) {
      return Number(value) ? sign + this.formatMoney(value) : this.formatMoney(0);
    },
    isNumeric(column) {
      return this.frIsNumericColumn(column);
    },
    params(extra) {
      return Object.assign(
        { captain_id: this.filters.captain_id, from: this.filters.from, to: this.filters.to },
        extra || {}
      );
    },
    loadReport() {
      if (!this.filters.captain_id) {
        window.getApp.$emit("SHOW_TOAST", this.t("chooseCaptainFirst"), "orange");
        return;
      }
      this.loading = true;
      this.axios
        .get(this.api.ACTIONS.ACCOUNTING_DRIVER_STATEMENT, { params: this.params() })
        .then((response) => {
          this.report = (response.data && response.data.data) || null;
          this.expanded = {};
          if (this.report && this.report.driver) {
            const d = this.report.driver;
            const parts = (d.name || "").split(" ");
            this.pickerPreset = {
              id: d.id,
              first_name: parts.shift() || "",
              last_name: parts.join(" "),
              username: d.phone || "",
            };
          }
        })
        .catch((error) => {
          window.getApp.$emit("SHOW_TOAST", this.frErrorMessage(error, this.c("failedLoad")), "red");
        })
        .finally(() => {
          this.loading = false;
        });
    },
    toggle(id) {
      const next = Object.assign({}, this.expanded);
      if (next[id]) {
        delete next[id];
      } else {
        next[id] = true;
      }
      this.expanded = next;
    },
    expandAll() {
      const next = {};
      this.trips.forEach((trip) => {
        next[trip.accounting_id] = true;
      });
      this.expanded = next;
    },
    collapseAll() {
      this.expanded = {};
    },
    percentText(trip) {
      const p = (v) => (v === null || v === undefined ? "—" : Number(v) + "%");
      return p(trip.company_percentage) + " / " + p(trip.driver_percentage) + " / " + p(trip.owner_percentage);
    },
    tripDetail(trip) {
      const items = [
        { label: this.t("detail.bookingDate"), value: this.formatStamp(trip.booking_datetime, "—") },
        { label: this.t("detail.settledAt"), value: this.formatStamp(trip.settled_at, "—") },
        { label: this.t("detail.typeSource"), value: this.t("typeSource." + trip.type_source) },
        { label: this.t("detail.percentages"), value: this.percentText(trip) },
        { label: this.t("detail.companyCommission"), value: this.money(trip.company_commission), numeric: true },
        { label: this.t("detail.companyKept"), value: this.money(trip.company_kept), numeric: true },
        { label: this.t("detail.toPay"), value: this.money(trip.to_pay), numeric: true },
        { label: this.t("detail.discount"), value: this.money(trip.discount), numeric: true },
        { label: this.t("detail.cashCollected"), value: this.money(trip.cash_collected), numeric: true },
      ];
      if (trip.journal_entry_id) {
        items.push({ label: this.t("detail.journalEntry"), value: trip.journal_reference || "#" + trip.journal_entry_id });
        items.push({ label: this.t("detail.journalDate"), value: trip.journal_date || "—" });
        items.push({ label: this.t("detail.glEffect"), value: this.money(trip.gl_effect), numeric: true, warn: trip.gl_mismatch });
        items.push({ label: this.t("detail.expectedEffect"), value: this.money(trip.expected_gl_effect), numeric: true });
      } else {
        items.push({ label: this.t("detail.journalEntry"), value: this.t("detail.noEntry"), warn: true });
      }
      return items;
    },
    moveRemark(move) {
      const parts = [];
      if (!move.gl_posted) parts.push(this.t("flag.notPosted"));
      if (move.in_trip_booking_id) parts.push(this.t("flag.inTrip") + " #" + move.in_trip_booking_id);
      if (move.is_reversal) parts.push(this.t("flag.reversal"));
      if (move.is_reversed) parts.push(this.t("flag.reversed"));
      return parts.join(" - ");
    },

    // ---------------- exports ----------------
    header() {
      return (this.report && this.report.header) || this.frBuildHeader({ title: this.t("title"), period: this.params() });
    },
    tripExportRows() {
      const rows = this.trips.map((trip) =>
        Object.assign({}, trip, {
          trip: "#" + trip.booking_id + " - " + this.formatStamp(trip.settled_at, ""),
          // The channel the fare actually went through (business, unpaid,
          // unconfirmed online settled as cash, wallet + cash...), then the
          // stored method.
          payment_label:
            (trip.payment_label || trip.payment_method_name || "") + (trip.payment_detail ? " (" + trip.payment_detail + ")" : ""),
          // Her table prints the fine as a negative amount.
          fine: trip.fine ? -trip.fine : 0,
          _highlight: trip.gl_mismatch ? "warn" : undefined,
        })
      );
      if (this.trips.length) {
        rows.push(
          Object.assign({}, this.totals, {
            trip: this.c("total"),
            type_label: "",
            payment_label: "",
            driver_account_code: "",
            owner_account_code: "",
            fine: this.totals.fine ? -this.totals.fine : 0,
            _kind: "total",
          })
        );
      }
      return rows;
    },
    otherExportRows() {
      const rows = this.otherMovements.map((move) =>
        Object.assign({}, move, { remark: this.moveRemark(move), _highlight: move.gl_posted ? undefined : "warn" })
      );
      if (rows.length) {
        rows.push({
          journal_date: this.c("total"),
          debit: this.report.other_totals.debit,
          credit: this.report.other_totals.credit,
          _kind: "total",
        });
      }
      return rows;
    },
    driverExportRows() {
      return this.driverLines.map((line) => ({ field: line.label, value: line.value + (line.hint ? " (" + line.hint + ")" : "") }));
    },
    infoColumns() {
      return [
        { key: "field", label: this.t("field"), type: "text", width: 30 },
        { key: "value", label: this.t("value"), type: "text", width: 60 },
      ];
    },
    recColumns() {
      return [
        { key: "label", label: this.t("field"), type: "text", width: 50 },
        { key: "amount", label: this.t("value"), type: "money" },
      ];
    },
    recExportRows() {
      const rows = this.reconciliationRows.map((row) => Object.assign({}, row));
      rows.push({
        label: this.report.reconciliation.is_reconciled ? this.t("rec.reconciled") : this.t("rec.notReconciled"),
        amount: "",
        _highlight: this.report.reconciliation.is_reconciled ? "ok" : "error",
      });
      return rows;
    },
    allNotes() {
      if (!this.report) {
        return [];
      }
      return (this.report.alerts || []).map((alert) => alert.text).concat(this.report.notes || []);
    },
    fileName() {
      return "driver-statement-" + this.filters.captain_id;
    },
    exportExcel() {
      if (!this.report) return;
      this.frExportXlsx({
        fileName: this.fileName(),
        header: this.header(),
        sheets: [
          { name: this.t("sheetDriver"), title: this.t("driverInfo"), columns: this.infoColumns(), rows: this.driverExportRows(), notes: this.allNotes() },
          { name: this.t("sheetTrips"), title: this.t("tripsTitle"), columns: this.tripColumns, rows: this.tripExportRows() },
          { name: this.t("sheetOther"), title: this.t("otherTitle"), columns: this.otherColumns, rows: this.otherExportRows() },
          {
            name: this.t("sheetBalances"),
            title: this.t("balancesTitle"),
            columns: this.balanceColumns,
            rows: this.balanceRows,
          },
          {
            name: this.t("reconciliationTitle"),
            title: this.t("reconciliationTitle"),
            columns: this.recColumns(),
            rows: this.recExportRows(),
          },
        ],
      });
    },
    exportPrint() {
      if (!this.report) return;
      this.frPrint({
        header: this.header(),
        orientation: "landscape",
        sections: [
          { title: this.t("driverInfo"), columns: this.infoColumns(), rows: this.driverExportRows() },
          { title: this.t("tripsTitle"), columns: this.tripColumns, rows: this.tripExportRows() },
          { title: this.t("otherTitle"), columns: this.otherColumns, rows: this.otherExportRows() },
          { title: this.t("balancesTitle"), columns: this.balanceColumns, rows: this.balanceRows },
          { title: this.t("reconciliationTitle"), columns: this.recColumns(), rows: this.recExportRows() },
        ],
        notes: this.allNotes(),
      });
    },
    downloadCsv() {
      if (!this.filters.captain_id) return;
      this.frDownloadCsv(this.api.ACTIONS.ACCOUNTING_DRIVER_STATEMENT, this.params(), this.fileName());
    },
  },
};
</script>

<style scoped>
.ds-empty {
  padding: 32px;
  text-align: center;
  color: rgba(0, 0, 0, 0.55);
}
.ds-alert-warn {
  margin: 8px 12px 0;
  padding: 8px 12px;
  border-radius: 4px;
  background: #fff3e0;
  color: #e65100;
  font-size: 13px;
}
.ds-section {
  padding: 8px 12px 12px;
}
.ds-section-title {
  font-weight: 700;
  font-size: 14px;
  margin: 6px 0 8px;
}
.ds-count {
  font-weight: 400;
  color: rgba(0, 0, 0, 0.55);
  font-size: 12px;
}
.ds-info-grid,
.ds-detail-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 8px 16px;
}
.ds-info-item,
.ds-detail-item {
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  padding: 6px 10px;
  background: #fafafa;
}
.ds-info-label {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.55);
}
.ds-info-value {
  font-weight: 600;
}
.ds-info-hint {
  font-size: 11px;
  color: #e65100;
}
/* Table chrome comes from the shared .fin-table layer (finance-compact.css,
   final-version doc 2026-10-05). */
.ds-table-wrap {
  --fin-table-max-h: calc(100vh - 260px);
  min-height: 0;
  margin: 0;
}
.ds-table {
  font-size: 12.5px;
}
.ds-table th,
.ds-table td {
  padding: 5px 8px;
  vertical-align: top;
}
/* Long trip-column headers wrap instead of widening the table. */
.ds-table thead th {
  white-space: normal;
}
.ds-sub {
  font-size: 11px;
  color: rgba(0, 0, 0, 0.5);
}
.ds-wrap {
  white-space: normal;
  min-width: 200px;
}
.ds-bold {
  font-weight: 600;
}
.ds-plus {
  color: #2e7d32;
}
.ds-minus {
  color: #c62828;
}
.ds-extra {
  background: #fff8e1;
}
.ds-toggle-col {
  width: 34px;
  padding: 0 2px !important;
}
.ds-toggle {
  margin: 0;
  height: 22px;
  width: 22px;
}
.ds-detail-row td {
  background: #f5f7fa;
  white-space: normal;
}
.ds-row-warn td {
  background: #fff3e0;
}
.ds-row-muted td {
  color: rgba(0, 0, 0, 0.45);
  font-style: italic;
}
.ds-warn-text {
  color: #d84315;
  font-weight: 600;
}
.ds-total-row td {
  font-weight: 700;
  background: #eceff1;
}
.ds-empty-cell {
  text-align: center !important;
  padding: 18px !important;
  color: rgba(0, 0, 0, 0.5);
}
.ds-two-col {
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 2fr);
  gap: 16px;
}
@media (max-width: 1100px) {
  .ds-two-col {
    grid-template-columns: minmax(0, 1fr);
  }
}
.ds-ok {
  color: #2e7d32;
  font-weight: 700;
}
.ds-bad {
  color: #c62828;
  font-weight: 700;
}
.ds-notes ul {
  margin: 0;
  padding-inline-start: 20px;
  font-size: 12.5px;
  color: rgba(0, 0, 0, 0.7);
}
@media print {
  .fr-no-print {
    display: none !important;
  }
}
</style>
